<template>
  <div>
    <div>
      <div class="survey-table">
        <b-modal v-model="wipeconfirm" hide-footer title="Delete survey permanently?">
          <p v-if="wipesurvey">
            WARNING! This operation will physically delete ALL information for this survey,
            including survey participants, answers to survey questions, and all reports.
          </p>
          <p v-if="wipesurveys.length > 0">
            WARNING! This operation will physically delete ALL information for the selected surveys,
            including survey participants, answers to survey questions, and all reports.
            <br />
            <br />
            This can take a long time depending on the amount of surveys selected. Please do not close the browser
            window.
          <ul class="mt-3">
            <li v-for="survey in wipesurveys" class="mb-1">{{ survey.name }} ({{ survey.id }})</li>
          </ul>
          </p>
          <p><strong>THIS OPERATION CANNOT BE REVERSED.</strong></p>
          <b-button class="mt-3" variant="danger" block @click="survey_deletefromdatabase(wipesurvey)">
            Delete
          </b-button>
          <b-button class="mt-2" variant="secondary" block @click="wipeconfirm = false; wipesurvey = false;">
            Cancel
          </b-button>
        </b-modal>
        <b-modal v-model="wipeprogress" hide-footer title="Permanently deleting survey(s)">
          <b-row>
            <div class="col-12 text-center">
              <h5>Deleting survey(s)</h5>
            </div>
          </b-row>
          <div class="col-12 text-center">
            <b-spinner style="width: 4rem; height: 4rem;" variant="danger"></b-spinner>
          </div>
        </b-modal>
        <b-modal id="deleteconfirm" hide-footer>
          D ya?
        </b-modal>

        <div class="table-filter-area">
          <b-row>
            <div class="col-md-8 pt-4">
              <b-form-group>
                <b-input type="text" v-model="search" @input="handleSearch" placeholder="Search" />
              </b-form-group>
            </div>
          </b-row>
          <div class="table-button-area mb-3">
            <div class="d-flex justify-content-between">
              <div>
                <button class="btn btn-small"
                  :class="{ 'btn-primary': survey_status_id === 3, 'btn-secondary': survey_status_id !== 3 }"
                  @click="filter_table(3)">
                  Ongoing ({{ surveycount_3 }})
                </button>
                <button class="btn btn-small"
                  :class="{ 'btn-primary': survey_status_id === 1, 'btn-secondary': survey_status_id !== 1 }"
                  @click="filter_table(1)">
                  Drafts ({{ surveycount_1 }})
                </button>
                <button class="btn btn-small"
                  :class="{ 'btn-primary': survey_status_id === 2, 'btn-secondary': survey_status_id !== 2 }"
                  @click="filter_table(2)">
                  Scheduled ({{ surveycount_2 }})
                </button>
                <button class="btn btn-small"
                  :class="{ 'btn-primary': survey_status_id === 4, 'btn-secondary': survey_status_id !== 4 }"
                  @click="filter_table(4)">
                  Completed ({{ surveycount_4 }})
                </button>
                <button class="btn btn-small"
                  :class="{ 'btn-primary': survey_status_id === 5, 'btn-secondary': survey_status_id !== 5 }"
                  @click="filter_table(5)">
                  Trash ({{ surveycount_5 }})
                </button>
              </div>
              <div class="text-right">
                <b-link to="/create">
                  <button class="btn btn-small btn-primary"><i class="fa fa-regular fa-plus mr-2"></i>New survey</button>
                </b-link>
                <button class="btn btn-small btn-secondary" @click="clearFilters">Clear filter</button>
                <button class="btn btn-small btn-secondary" @click.prevent="wipeconfirm = true"
                  :disabled="wipesurveys.length === 0">
                  Delete
                </button>
              </div>
            </div>
          </div>

        </div>
        <b-table
          responsive
          hover
          :busy="loading"
          :items="surveys"
          :fields="fields"
          class="table surveytable"
          @row-clicked="openSurveyPage"
        >
          <template v-slot:head(select)>
            <b-form-checkbox :checked="allSelected" @change="selectall($event)" />
          </template>

          <template v-slot:cell(select)="data">
            <div>
              <b-form-checkbox v-model="data.item.selected" @change="countselected" />
            </div>
          </template>

          <template #table-busy>
            <div class="text-center text-success my-2">
              <b-spinner class="align-middle mr-2" />
              <strong>Loading...</strong>
            </div>
          </template>

          <template v-slot:cell(survey_name)="data">
            <span :class="`${anon ? 'g-anon' : ''}`">
              <i class="fa-regular fa-square-poll-vertical"></i>&nbsp;&nbsp;{{ data.item.survey_name }} ({{ data.item.id }})
            </span>
          </template>

          <template v-slot:cell(group_id)="data">
            <span :class="`${anon ? 'g-anon' : ''}`">
              {{ data.item.group_id }}
            </span>
          </template>

          <template v-slot:cell(survey_type_id)="data">
            {{ data.value }}
          </template>

          <template v-slot:cell(survey_status)="data">
            <span :class="getClassColor('status', data.item)" class="pill">{{ surveyTypeName(data.item.survey_status_id)
              }}
            </span>
          </template>

          <template v-slot:cell(survey_start_date)="data">
            {{ data.item.survey_start_date > '1970-01-02' ? format_date(data.item.survey_start_date) : 'Not set' }}
          </template>

          <template v-slot:cell(survey_end_date)="data">
            {{ data.item.survey_end_date > '1970-01-02' ? format_date(data.item.survey_end_date) : 'Not set'}}
          </template>

          <template v-slot:cell(survey_replied)="data">
            <span :class="getClassColor('member', data.item)" class="pill">{{ data.item.completecount }}/{{
              data.item.membercount }}</span>
          </template>

          <template v-slot:cell(actions)="data">
            <b-dropdown right class="w-100">
              <template #button-content>
                <i class="fa-solid fa-ellipsis"></i>
              </template>
              <b-dropdown-item @click.prevent="openSurveyPageId(data.item)">Edit</b-dropdown-item>
              <b-dropdown-item v-if="survey_status_id != 5"
                @click.prevent="survey_delete(data.item.id, data.item.survey_deleted, data.item.survey_status_id, data.item.completecount)">Trash</b-dropdown-item>
              <b-dropdown-item v-if="survey_status_id == 5"
                @click.prevent="survey_delete(data.item.id, data.item.survey_deleted, data.item.survey_status_id, data.item.completecount)">Restore</b-dropdown-item>
              <b-dropdown-item @click.prevent="wipeconfirm = true; wipesurvey = data.item.id">
                Delete
              </b-dropdown-item>

            </b-dropdown>
          </template>

        </b-table>
      </div>
    </div>
  </div>
</template>
<script>
import SurveyProgressBar from './SurveyProgressBar.vue'
import Breadcrumb from './Breadcrumb.vue'
import Navbar from './Navbar.vue'

export default {
  components: {
    SurveyProgressBar,
    Navbar,
    Breadcrumb
  },
  props: {
    surveytype: {
      type: String,
      default: ''
    },
    groupid: {
      type: String,
      default: 0
    }
  },
  data () {
    return {
      wipeconfirm: false,
      wipeprogress: false,
      wipesurvey: 0,
      wipesurveys: [],
      consultant_id: this.$store.state.user_id,
      group_id: this.$props.groupid, // this.$store.state.currentgroup.id,
      loading: true,
      selected: 0,
      surveysselected: 0,
      surveys_total: 0,
      surveys_retrieved: 10,
      survey_status_id: 3,
      allSelected: false,
      search: '',
      groupFilter: 0,
      fields: [
        { key: 'select', label: 'S', sortable: false, tdClass: 'select-td', thClass: 'select-th' },
        { key: 'survey_name', sortable: true, label: 'Survey name', tdClass: 'w-50' },
        { key: 'group_id', sortable: true, label: 'Group', tdClass: 'w-50' },
        { key: 'survey_type_id', sortable: true, label: 'Type' },
        { key: 'survey_status', sortable: true, label: 'Status' },
        { key: 'survey_start_date', sortable: true, label: 'Start date' },
        { key: 'survey_end_date', sortable: true, label: 'End date' },
        { key: 'survey_replied', sortable: false, label: 'Replied' },
        { key: 'actions', label: 'Actions', class: 'ellipsis-button' }
      ],
      surveycount_1: 0,
      surveycount_2: 0,
      surveycount_3: 0,
      surveycount_4: 0,
      surveycount_5: 0,
      deleteconfirm: 1,
      surveys: []
    }
  },
  computed: {
    anon: function () {
      return this.$store.state.anon
    },
    mygdqpage_reload: {
      get () {
        return this.$store.state.mygdqpage_reload
      }
    },
    setadmin_done: {
      get () {
        return this.$store.state.questionnaire_admin_done
      }
    }
  },
  created () {
    this.loading = true
    this.surveys_get()
    this.$store.dispatch('questionnaire_setadmin', { admin: false })
    this.$store.dispatch('questionnaire_setadmin_code', { code: '' })
    this.$store.dispatch('questionnaire_setadmin_done', { done: false })
    this.surveycounts_get()
  },
  methods: {
    clearFilters () {
      this.search = ''
      this.survey_status_id = 3
      this.group_id = null
      this.allSelected = false
      this.surveys_get()
    },

    set_group () {
      this.surveys_get()
    },

    handleSearch () {
      clearTimeout(this.timeout)
      this.timeout = setTimeout(() => {
        this.surveys_get()
      }, 1000)
    },

    handleGroupChange (event) {
      this.groupFilter = event.target.value
      this.surveys_get()
    },

    filter_table (status_id) {
      this.survey_status_id = status_id
      this.allSelected = false
      this.surveys_get()
    },

    surveyTypeName (id) {
      id = Number(id)
      switch (id) {
        case 1:
          return 'Draft'
        case 2:
          return 'Scheduled'
        case 3:
          return 'Ongoing'
        case 4:
          return 'Completed'
        default:
          return 'Default'
      }
    },
    async survey_deletefromdatabase () {
      this.wipeconfirm = false
      this.wipeprogress = true

      if (this.wipesurvey) {
        await this.$store.dispatch('groupsurvey_wipe', { id: this.wipesurvey })
        this.wipesurvey = 0
      } else if (this.wipesurveys.length > 0) {
        for (var i = 0; i < this.wipesurveys.length; i++) {
          const id = this.surveys[i].id
          await this.$store.dispatch('groupsurvey_wipe', { id })
        }
        this.wipesurveys = []
      }
      this.loading = true
      this.surveys_get()
      this.surveycounts_get()
      this.wipeprogress = false
    },
    async surveycounts_get () {
      const filter = {
        group_id: {
          operator: '=',
          value: this.group_id,
          realfield: 1
        },
        survey_deleted: { operator: '=', value: 0, realfield: 1 } }
      this.surveycount_1 = await this.$store.dispatch('surveys_fetch', {
        filter: { ...filter, survey_status_id: { operator: '=', value: 1, realfield: 1 } },
        countonly: 1
      }).then(r => { return r.data.total })
      this.surveycount_2 = await this.$store.dispatch('surveys_fetch', {
        filter: { ...filter, survey_status_id: { operator: '=', value: 2, realfield: 1 } },
        countonly: 1
      }).then(r => { return r.data.total })
      this.surveycount_3 = await this.$store.dispatch('surveys_fetch', {
        filter: { ...filter, survey_status_id: { operator: '=', value: 3, realfield: 1 } },
        countonly: 1
      }).then(r => { return r.data.total })
      this.surveycount_4 = await this.$store.dispatch('surveys_fetch', {
        filter: { ...filter, survey_status_id: { operator: '=', value: 4, realfield: 1 } },
        countonly: 1
      }).then(r => { return r.data.total })
      this.surveycount_5 = await this.$store.dispatch('surveys_fetch', {
        filter: { group_id: { operator: '=', value: this.group_id, realfield: 1 }, survey_deleted: { operator: '=', value: 1, realfield: 1 } },
        countonly: 1
      }).then(r => { return r.data.total })
    },
    survey_delete (id, survey_deleted, survey_status_id, completecount) {
      this.deleteconfirm = 1
      if (survey_deleted == 0) {
        if (
          confirm(
            'Are you sure you want to move this survey to trash?'
          )
        ) {
          this.deleteconfirm = 1
        } else {
          this.deleteconfirm = 0
        }
      }
      if (this.deleteconfirm == 1) {
        var newstatus = 1 - survey_deleted
        var survey = {
          id: id,
          survey_deleted: newstatus
        }
        this.$store
          .dispatch('groupsurvey_update', { survey: survey })
          .then(resp => {
            this.surveys_get()
            this.surveycounts_get()
          })
      }
    },
    selectall (event) {
      this.allSelected = event
      this.surveys.forEach(survey => {
        survey.selected = this.allSelected
      })
      this.countselected()
    },
    countselected () {
      var selectedsurveys = []
      var numbersel = 0
      for (var i = 0; i < this.surveys.length; i++) {
        if (this.surveys[i].selected) {
          selectedsurveys.push({ id: this.surveys[i].id, name: this.surveys[i].survey_name })
          numbersel++
        }
      }
      this.surveysselected = numbersel
      this.wipesurveys = selectedsurveys
      this.$store.dispatch('groupsurveys_setselected', {
        surveys: selectedsurveys
      })
    },
    groups_get: function () {
      this.$store.dispatch('groups_fetch')
    },
    surveys_get () {
      this.loading = true
      let filter = {
        user_id: {
          operator: '=',
          value: this.consultant_id,
          realfield: 1
        },
        survey_deleted: {
          operator: '=',
          value: 0,
          realfield: 1
        },
        group_id: {
          operator: '=',
          value: this.group_id,
          realfield: 1
        }
      }
      if (this.survey_status_id) {
        filter.survey_status_id = {
          operator: '=',
          value: this.survey_status_id,
          realfield: 1
        }
      }
      if (this.survey_status_id == 5) {
        filter = {
          user_id: {
            operator: '=',
            value: this.consultant_id,
            realfield: 1
          },
          survey_deleted: {
            operator: '=',
            value: 1,
            realfield: 1
          },
          group_id: {
            operator: '=',
            value: this.group_id,
            realfield: 1
          }
        }
      }
      if (this.search) {
        filter.survey_name = {
          operator: 'LIKE',
          value: `*${this.search}*`,
          realfield: 1
        }
      }
      this.$store.dispatch('surveys_fetch', { filter, countonly: false }).then((resp) => {
        this.surveys = resp.data.result
        this.surveys_retrieved = resp.data.result.length
        this.surveys_total = resp.data.total
        this.loading = false
      })
    },
    openSurveyPage (item, index, event) {
      const element = event.target
      const includedTags = ['span', 'i']
      if (includedTags.includes(element.tagName.toLowerCase())) {
        this.$store
          .dispatch('groupsurvey_setcurrent', { survey_id: item.id })
          .then(() => {
            if (item.survey_status_id == 1) {
              this.$router.push('/create/group/survey/details')
            } else {
              this.$router.push('/group/survey')
            }
          })
      }

      const isFirstElement = !element.previousSibling
      const isLastElement = !element.nextSibling

      if (!isLastElement && !isFirstElement) {
        this.$store
          .dispatch('groupsurvey_setcurrent', { survey_id: item.id })
          .then(() => {
            if (item.survey_status_id == 1) {
              this.$router.push('/create/group/survey/details')
            } else {
              this.$router.push('/group/survey')
            }
          })
      }
    },

    openSurveyPageId (item) {
      this.$store
        .dispatch('groupsurvey_setcurrent', { survey_id: item.id })
        .then(() => {
          if (item.survey_status_id == 1) {
            this.$router.push('/create/group/survey/details')
          } else {
            this.$router.push('/group/survey')
          }
        })
    },

    getClassColor (type, survey) {
      if (type == 'member') {
        const { completecount, membercount } = survey
        if (completecount === 0) {
          return 'bg-danger'
        } else if (completecount > 0 && completecount < membercount) {
          return 'bg-warning'
        } else if (completecount === membercount) {
          return 'bg-secondary'
        }
      } else if (type == 'status') {
        const status_id = Number(survey.survey_status_id)
        switch (status_id) {
          case 1:
            return 'bg-danger'
          case 2:
            return 'bg-secondary'
          case 3:
            return 'bg-warning'
          case 4:
            return 'bg-primary'
          default:
            return ''
        }
      }
      return ''
    },
    format_date (date) {
      var d = new Date(date)
      var month = '' + (d.getMonth() + 1)
      var day = '' + d.getDate()
      var year = d.getFullYear()
      if (month.length < 2) month = '0' + month
      if (day.length < 2) day = '0' + day
      return [year, month, day].join('-')
    },
    format_time (date) {
      var d = new Date(date)
      var h = d.getHours()
      var m = d.getMinutes()
      var hour = (h > 9 ? '' : '0') + h
      var minute = (m > 9 ? '' : '0') + m
      var thistime = hour + ':' + minute
      return thistime
    }
  }
}
</script>
<style>
td {
  cursor: pointer !important;
}
</style>
